// Shared helpers for the installation image upload modal.
// Matching rules mirror new-report-service (executions/installation-images/headerMaster.js).

export const OTHER_HEADER = { headerName: 'Other', label: 'other', isMandatory: false }

/** "Vehicle Photo Front View" / "vehicle_photo_front_view" -> "vehiclephotofrontview" */
export const normaliseKey = value =>
  String(value === null || value === undefined ? '' : value)
    .toLowerCase()
    .replace(/[‐-―−]/g, '-')
    .replace(/[^a-z0-9]/g, '')

// Solution types are solution_type_master ids (getAllMasterSolutionType), used exactly as stored.
export const solutionId = value => String(value === null || value === undefined ? '' : value).trim()

/** Display name from the master list; falls back to the id itself. */
export const solutionLabel = (solution, solutionNames) => (solutionNames && solutionNames[solution]) || solution

/** Headers of one solution; every solution always offers an optional "Other". */
export const headersForSolution = (headersBySolution, solution) => {
  const list = (headersBySolution[solution] || []).slice()
  if (!list.some(h => normaliseKey(h.headerName) === normaliseKey(OTHER_HEADER.headerName))) {
    list.push({ ...OTHER_HEADER, solutionType: solution, displayOrder: 9999 })
  }
  return list
}

/** Header-name match first, label as fallback (label may be null). */
export const findHeader = (headers, label) => {
  const key = normaliseKey(label)
  if (!key) return null
  return (
    headers.find(h => normaliseKey(h.headerName) === key) ||
    headers.find(h => h.label && normaliseKey(h.label) === key) ||
    null
  )
}

/** Label saved for a header picked in the modal: TMA-style label when configured, else the header name. */
export const labelForHeader = header => (header.label ? header.label : header.headerName)

/**
 * Edit page: stored documentUploadNew + legacy documentUpload names, in a single list.
 * Stored entries keep every field exactly as received; legacy-only names get empty metadata.
 */
export const buildInitialEntries = (documentUploadNew, documentUpload) => {
  let stored = documentUploadNew
  if (typeof stored === 'string') {
    try {
      stored = JSON.parse(stored)
    } catch (e) {
      stored = []
    }
  }
  const entries = []
  const seen = {}
  ;(Array.isArray(stored) ? stored : []).forEach(doc => {
    const fileName = doc && typeof doc.fileName === 'string' ? doc.fileName.trim() : ''
    if (!fileName || seen[fileName]) return
    seen[fileName] = true
    entries.push({ ...doc, fileName })
  })
  String(documentUpload || '')
    .split(',')
    .map(n => n.trim())
    .filter(Boolean)
    .forEach(fileName => {
      if (seen[fileName]) return
      seen[fileName] = true
      entries.push({ fileName, solutionType: '', label: '' })
    })
  return entries
}

/** Entries -> the state the existing payload code already reads (fileNames + documentUploadNewData). */
export const entriesToState = entries => {
  const documentUploadNewData = {}
  const uploadProgress = {}
  entries.forEach(entry => {
    const { fileName, ...meta } = entry
    documentUploadNewData[fileName] = meta
    uploadProgress[fileName] = 100
  })
  return {
    fileNames: entries.map(e => e.fileName).join(','),
    documentUploadNewData,
    uploadProgress
  }
}

/** Current state -> entries for the modal (keeps metadata exactly as stored in state). */
export const stateToEntries = (fileNames, documentUploadNewData) =>
  String(fileNames || '')
    .split(',')
    .map(n => n.trim())
    .filter(Boolean)
    .map(fileName => ({
      ...((documentUploadNewData && documentUploadNewData[fileName]) || { solutionType: '', label: '' }),
      fileName
    }))

let rowSeq = 0
export const newRowId = () => {
  rowSeq += 1
  return `r${rowSeq}`
}

/**
 * Builds solution blocks + unassigned list from the current entries.
 * knownSolutions: solution_type_master ids (and ids present in installation_image_headers).
 */
export const buildImageModel = (entries, vehicleSolutionType, headersBySolution, mode, knownSolutions) => {
  const blocks = []
  const isNewBlock = mode === 'add'
  const isKnown = solution => !!solution && (!knownSolutions || knownSolutions.indexOf(solution) !== -1)
  const ensureBlock = solution => {
    let block = blocks.find(b => b.solution === solution)
    if (!block) {
      const headers = headersForSolution(headersBySolution, solution)
      const mandatory = headers.filter(h => h.isMandatory)
      // A solution with no checklist starts with one "Other" slot (at least one image is required)
      const initial = mandatory.length ? mandatory : headers.filter(h => normaliseKey(h.headerName) === 'other')
      block = {
        id: newRowId(),
        solution,
        isNew: isNewBlock,
        rows: initial.map(header => ({ id: newRowId(), header, fileName: '', original: null, dirty: false }))
      }
      blocks.push(block)
    }
    return block
  }

  // The vehicle's own solution type (e.g. "fuel+vt") is the first block.
  const vehicleSolution = solutionId(vehicleSolutionType)
  if (vehicleSolution) ensureBlock(vehicleSolution)

  const unassigned = []
  const placeInBlock = (block, entry) => {
    const header = findHeader(headersForSolution(headersBySolution, block.solution), entry.label)
    if (!header) return false
    const emptyMandatory = header.isMandatory && block.rows.find(r => r.header === header && !r.fileName)
    if (emptyMandatory) {
      emptyMandatory.fileName = entry.fileName
      emptyMandatory.original = entry
    } else {
      block.rows.push({ id: newRowId(), header, fileName: entry.fileName, original: entry, dirty: false })
    }
    return true
  }

  const mandatoryKeys = solution =>
    headersForSolution(headersBySolution, solution)
      .filter(h => h.isMandatory)
      .map(h => normaliseKey(h.headerName))
  // Same rule as the Dtwin API: an image's own solution type gets its own block unless that
  // checklist is already inside the vehicle's (fuel_solution images of a fuel+vt vehicle stay put).
  const hasSeparateChecklist = solution => {
    if (!solution || solution === vehicleSolution) return false
    const own = mandatoryKeys(solution)
    if (!own.length) return false
    const vehicleKeys = vehicleSolution ? mandatoryKeys(vehicleSolution) : []
    return !own.every(k => vehicleKeys.indexOf(k) !== -1)
  }

  entries.forEach(entry => {
    if (!normaliseKey(entry.label)) {
      unassigned.push({ id: newRowId(), entry, solution: '', headerName: '' })
      return
    }
    const solution = solutionId(entry.solutionType)
    let ownBlock = blocks.find(b => b.solution === solution)
    let createdOwn = false
    if (!ownBlock && isKnown(solution) && hasSeparateChecklist(solution)) {
      ownBlock = ensureBlock(solution)
      createdOwn = true
    }
    // 1) block of the image's own solution type, 2) the vehicle's blocks (a fuel+vt vehicle holds
    // images tagged fuel_solution / video_telematics), 3) a new block for a known solution type.
    let placed = !!ownBlock && placeInBlock(ownBlock, entry)
    if (!placed && createdOwn && !ownBlock.rows.some(r => r.fileName)) {
      blocks.splice(blocks.indexOf(ownBlock), 1)
      ownBlock = undefined
    }
    if (!placed) placed = blocks.some(b => b !== ownBlock && placeInBlock(b, entry))
    if (!placed && !ownBlock && isKnown(solution)) {
      const block = ensureBlock(solution)
      placed = placeInBlock(block, entry)
      if (!placed && !block.rows.some(r => r.fileName)) blocks.splice(blocks.indexOf(block), 1)
    }
    if (!placed) unassigned.push({ id: newRowId(), entry, solution: '', headerName: '' })
  })

  return { blocks, unassigned }
}

/**
 * Modal state -> documentUploadNew entries.
 * Untouched stored images are returned exactly as received; new or re-assigned ones get
 * the block's solution type and the header's label.
 */
export const collectEntries = (blocks, unassigned) => {
  const result = []
  const seen = {}
  const push = entry => {
    if (!entry.fileName || seen[entry.fileName]) return
    seen[entry.fileName] = true
    result.push(entry)
  }
  blocks.forEach(block =>
    block.rows.forEach(row => {
      if (!row.fileName || !row.header) return
      if (row.original && !row.dirty && row.original.fileName === row.fileName) push(row.original)
      else push({ fileName: row.fileName, solutionType: block.solution, label: labelForHeader(row.header) })
    })
  )
  unassigned.forEach(item => push(item.entry))
  return result
}



// WEBPACK FOOTER //
// ./src/components/Modules/VehicleRegistartion/installationImageUtils.js